import { useState } from "react";
import { toast } from "react-toastify";
import { formatarData, formatarMoeda } from "../../../utils/solicitacoes/formatters";
import RateioOrcamentarioTabela from "../Shared/RateioOrcamentarioTabela";
import VeiculoFleetcomModal from "../Shared/VeiculoFleetcomModal";
import Campo from "../Shared/Campo";

const copiarTexto = async (texto, label) => {
    try {
        await navigator.clipboard.writeText(String(texto));
        toast.success(`${label} copiado`, { autoClose: 500 });
    } catch {
        toast.error("Não foi possível copiar");
    }
};

const TIPO_DESPESA_LABEL = {
    SUPRIMENTOS: "Suprimentos",
    CONTAS_A_PAGAR: "Contas a Pagar",
};

// Dados acumulados do processo — sempre visível, independentemente da etapa atual (diferente do
// componente de "Etapa atual", que só mostra o que é acionável naquele momento). Cada seção só
// aparece quando a etapa correspondente já foi preenchida.
export default function DetalheCompras({ detalheProcesso }) {
    const [placaSelecionada, setPlacaSelecionada] = useState(null);

    if (!detalheProcesso) return null;

    const endereco = [detalheProcesso.filialEnderecoLogradouro, detalheProcesso.filialEnderecoBairro].filter(Boolean).join(", ");
    const cidadeUf = [detalheProcesso.filialEnderecoCidade, detalheProcesso.filialEnderecoUf].filter(Boolean).join("/");

    const temCotacao = detalheProcesso.menorCotacao != null || detalheProcesso.valorFinalCotacao != null
        || detalheProcesso.fornecedores?.length > 0 || detalheProcesso.produtoEmEstoque === true
        || !!detalheProcesso.observacaoCotacao;

    // Produtos cotados, únicos por produtoId — usados como linhas da tabela comparativa (colunas = fornecedores).
    const produtosCotacao = [];
    const produtosCotacaoVistos = new Set();
    detalheProcesso.fornecedores?.forEach(f => f.produtosCotados?.forEach(item => {
        if (!produtosCotacaoVistos.has(item.produtoId)) {
            produtosCotacaoVistos.add(item.produtoId);
            produtosCotacao.push(item);
        }
    }));
    const temOrdemCompra = detalheProcesso.confirmacaoCompraRealizada != null || detalheProcesso.valorCompra != null
        || detalheProcesso.tipoPagamento || detalheProcesso.fornecedorNome || detalheProcesso.tipoDespesa || detalheProcesso.descricaoDespesa;
    const temRecebimento = detalheProcesso.dataRecebimento || detalheProcesso.valorNotaRecebida != null
        || detalheProcesso.pagamentoRealizado != null || detalheProcesso.numerosNotasFiscais?.length > 0
        || detalheProcesso.complementosRecebimento?.length > 0;

    return (
        <div className="bg-card border border-border rounded-lg shadow-card p-4 md:p-6 flex flex-col gap-4">
            {detalheProcesso.motivoPendencia && (
                <div className="flex items-start gap-2.5 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5">
                    <i className="fa fa-exclamation-triangle text-amber-500 text-sm mt-0.5 shrink-0" />
                    <div className="min-w-0">
                        <span className="block text-[11px] uppercase tracking-wide font-semibold text-amber-700">Motivo da pendência</span>
                        <span className="block text-sm text-amber-900">{detalheProcesso.motivoPendencia}</span>
                    </div>
                </div>
            )}

            <div>
                <h2 className="font-display text-base font-semibold text-foreground mb-4">Dados da Solicitação</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <Campo label="Tipo do produto" valor={detalheProcesso.tipoProdutoNome} />
                    <div className="col-span-2 md:col-span-3">
                        <Campo label="Motivo" valor={detalheProcesso.motivo} />
                    </div>
                </div>
            </div>

            {detalheProcesso.produtos?.length > 0 && (
                <div>
                    <h3 className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground mb-2">Produtos Solicitados</h3>
                    <div className="flex flex-col gap-1.5">
                        {detalheProcesso.produtos.map(produto => (
                            <div key={produto.id} className="flex items-start justify-between gap-3 text-sm bg-muted rounded-lg px-3 py-2">
                                <span className="flex flex-col items-start min-w-0 text-foreground">
                                    <button
                                        type="button"
                                        onClick={() => copiarTexto(produto.nome, "Nome do produto")}
                                        className="cursor-copy hover:text-primary text-left break-words"
                                        title="Clique para copiar o nome"
                                    >
                                        {produto.nome}
                                    </button>
                                    {produto.detalhes && (
                                        <button
                                            type="button"
                                            onClick={() => copiarTexto(produto.detalhes, "Detalhes do produto")}
                                            className="cursor-copy hover:text-primary text-left text-xs text-muted-foreground break-all"
                                            title="Clique para copiar os detalhes"
                                        >
                                            {produto.detalhes}
                                        </button>
                                    )}
                                </span>
                                <span className="flex items-center gap-3 shrink-0">
                                    {produto.placaVeiculo && (
                                        <button
                                            type="button"
                                            title="Ver dados do veículo na Fleetcom"
                                            onClick={() => setPlacaSelecionada(produto.placaVeiculo)}
                                            className="text-xs font-medium text-primary bg-primary/10 hover:bg-primary/20 rounded-full px-2 py-0.5"
                                        >
                                            Veículo: {produto.placaVeiculo}
                                        </button>
                                    )}
                                    <span className="font-medium text-foreground">Qtd: {produto.quantidade}{produto.unidadeMedida ? ` ${produto.unidadeMedida}` : ""}</span>
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {temCotacao && (
                <div>
                    <h3 className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground mb-2">
                        {detalheProcesso.necessitaCotacao === false ? "Cotação (informada na Solicitação)" : "Cotação"}
                    </h3>

                    {detalheProcesso.produtoEmEstoque && (
                        <p className="text-sm text-foreground bg-success/10 text-success rounded-lg px-3 py-2 mb-3">
                            <i className="fa fa-box-open mr-1.5" />
                            Produto já em estoque — solicitação finalizada sem necessidade de compra.
                        </p>
                    )}

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-3">
                        <Campo label="Menor cotação" valor={formatarMoeda(detalheProcesso.menorCotacao)} />
                        <Campo label="Valor final da Cotação" valor={formatarMoeda(detalheProcesso.valorFinalCotacao)} />
                    </div>

                    {detalheProcesso.observacaoCotacao && (
                        <div className="mb-3">
                            <Campo label="Observação" valor={detalheProcesso.observacaoCotacao} />
                        </div>
                    )}

                    {detalheProcesso.fornecedores?.length > 0 && (
                        <div className="overflow-x-auto border border-border rounded-lg">
                            <table className="w-full text-xs">
                                <thead className="bg-muted">
                                    <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                                        <th className="px-3 py-2 font-semibold">Produto</th>
                                        <th className="px-3 py-2 font-semibold text-center w-20">Qtde</th>
                                        {detalheProcesso.fornecedores.map((fornecedor, index) => (
                                            <th key={index} className="px-3 py-2 font-semibold text-right">
                                                {fornecedor.nome}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {produtosCotacao.map(produto => (
                                        <tr key={produto.produtoId} className="border-t border-border">
                                            <td className="px-3 py-2 text-foreground">{produto.produtoNome}</td>
                                            <td className="px-3 py-2 text-center text-foreground">{produto.quantidade}</td>
                                            {detalheProcesso.fornecedores.map((fornecedor, index) => {
                                                const item = fornecedor.produtosCotados?.find(i => i.produtoId === produto.produtoId);
                                                return (
                                                    <td key={index} className="px-3 py-2 text-right text-foreground">
                                                        {item ? formatarMoeda(item.valorUnitario) : "—"}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr className="border-t border-border bg-muted/40 font-semibold">
                                        <td className="px-3 py-2 text-foreground" colSpan={2}>Valor Total</td>
                                        {detalheProcesso.fornecedores.map((fornecedor, index) => (
                                            <td key={index} className="px-3 py-2 text-right text-foreground">{formatarMoeda(fornecedor.valorTotal)}</td>
                                        ))}
                                    </tr>
                                    <tr className="border-t border-border">
                                        <td className="px-3 py-2 text-foreground" colSpan={2}>Prazo de Entrega</td>
                                        {detalheProcesso.fornecedores.map((fornecedor, index) => (
                                            <td key={index} className="px-3 py-2 text-right text-foreground">{fornecedor.menorPrazo ? formatarData(fornecedor.menorPrazo) : "—"}</td>
                                        ))}
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {endereco && (
                <div>
                    <h3 className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground mb-2">Endereço de Entrega ({detalheProcesso.filialEntregaNome || detalheProcesso.filialNome})</h3>
                    <p className="text-sm text-foreground">
                        {endereco}, {cidadeUf && <>{cidadeUf}</>}
                        {detalheProcesso.filialEnderecoCep && <><br />CEP: {detalheProcesso.filialEnderecoCep}</>}
                    </p>
                </div>
            )}

            {temOrdemCompra && (
                <div>
                    <h3 className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground mb-2">Ordem de Compra</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        {detalheProcesso.confirmacaoCompraRealizada != null && (
                            <Campo label="Compra realizada" valor={detalheProcesso.confirmacaoCompraRealizada ? "Sim" : "Não"} />
                        )}
                        <Campo label="Valor da compra" valor={formatarMoeda(detalheProcesso.valorCompra)} />
                        <Campo label="Fornecedor" valor={detalheProcesso.fornecedorNome} />
                        <Campo label="Tipo da despesa" valor={TIPO_DESPESA_LABEL[detalheProcesso.tipoDespesa] || detalheProcesso.tipoDespesa} />
                        <Campo label="Tipo de pagamento" valor={detalheProcesso.tipoPagamento} />
                        <Campo label="Vencimento" valor={formatarData(detalheProcesso.vencimento)} />
                        {detalheProcesso.tipoPagamento === "Transferência" && (
                            <Campo label="Tipo de conta" valor={detalheProcesso.tipoConta} />
                        )}
                        {detalheProcesso.tipoPagamento === "Transferência" && (
                            <Campo label="Agência" valor={detalheProcesso.agencia + '-' + detalheProcesso.agenciaDigito} />
                        )}
                        {detalheProcesso.tipoPagamento === "Transferência" && (
                            <Campo label="Conta" valor={detalheProcesso.conta + '-' + detalheProcesso.contaDigito} />
                        )}
                        {detalheProcesso.tipoPagamento === "PIX" && (
                            <Campo label="Chave PIX" valor={detalheProcesso.chavePix} />
                        )}
                        <div className="col-span-2 md:col-span-4">
                            <Campo label="Descrição da despesa" valor={detalheProcesso.descricaoDespesa} />
                        </div>
                    </div>
                </div>
            )}

            {temRecebimento && (
                <div>
                    <h3 className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground mb-2">Recebimento</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <Campo label="Data de recebimento" valor={formatarData(detalheProcesso.dataRecebimento)} />
                        <Campo label="Valor total" valor={formatarMoeda(detalheProcesso.valorNotaRecebida)} />
                        <Campo label="Código de Lançamento do Fortes" valor={detalheProcesso.codigoLancamentoFortes} />
                        {detalheProcesso.pagamentoRealizado != null && (
                            <Campo label="Pagamento realizado" valor={detalheProcesso.pagamentoRealizado ? "Sim" : "Não"} />
                        )}
                    </div>
                    {detalheProcesso.numerosNotasFiscais?.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                            {detalheProcesso.numerosNotasFiscais.map((numero, index) => (
                                <span key={index} className="inline-flex items-center rounded-full bg-muted px-3 py-1 text-xs font-medium text-foreground">
                                    {numero}
                                </span>
                            ))}
                        </div>
                    )}

                    {detalheProcesso.complementosRecebimento?.length > 0 && (
                        <div className="mt-3">
                            <h4 className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground mb-2">Solicitações Complementares</h4>
                            <div className="overflow-x-auto border border-border rounded-lg">
                                <table className="w-full text-xs">
                                    <thead className="bg-muted">
                                        <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                                            <th className="px-3 py-2 font-semibold">Nº Solicitação</th>
                                            <th className="px-3 py-2 font-semibold">Solicitante</th>
                                            <th className="px-3 py-2 font-semibold text-right">Valor</th>
                                            <th className="px-3 py-2 font-semibold text-right">Data</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {detalheProcesso.complementosRecebimento.map((c, index) => (
                                            <tr key={index} className="border-t border-border">
                                                <td className="px-3 py-2 text-foreground">{c.numero}</td>
                                                <td className="px-3 py-2 text-foreground">{c.solicitanteNome}</td>
                                                <td className="px-3 py-2 text-right text-foreground">{formatarMoeda(c.valorUtilizado)}</td>
                                                <td className="px-3 py-2 text-right text-foreground">{formatarData(c.dataHora)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {detalheProcesso.rateioOrcamentarioInicial?.length > 0 && (
                <div>
                    <h3 className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground mb-2">Prévia do Rateio Orçamentário (Informado na Solicitação)</h3>
                    <RateioOrcamentarioTabela itens={detalheProcesso.rateioOrcamentarioInicial} />
                </div>
            )}

            {detalheProcesso.rateioOrcamentario?.length > 0 && (
                <div>
                    <h3 className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground mb-2">Rateio Orçamentário Final (Informado no Recebimento)</h3>
                    <RateioOrcamentarioTabela itens={detalheProcesso.rateioOrcamentario} />
                </div>
            )}

            <VeiculoFleetcomModal
                isOpen={!!placaSelecionada}
                placa={placaSelecionada}
                onClose={() => setPlacaSelecionada(null)}
            />
        </div>
    );
}
